import { Dialog, DialogClose, DialogTitle, useDialogClose } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Alert } from "~/components/alert";
import { Editor } from "~/components/code/editor";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormMessage } from "~/components/form/message";

import type { DashboardSpec } from "../../common/dashboard-spec";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";

import { init as initFormState } from "./_form-state";

export interface DashboardYamlDrawerProps {
	initial: DashboardSpec;
	onSave: (spec: DashboardSpec) => void;
}

export const DashboardYamlDrawer: React.FC<DashboardYamlDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide">
		<DashboardYamlDrawerContent {...props} />
	</Dialog>
);

const DashboardYamlDrawerContent: React.FC<DashboardYamlDrawerProps> = ({ initial, onSave }) => {
	const close = useDialogClose();
	const state = usePermanent(() => initFormState(initial));

	return (
		<Form
			className="contents"
			onSubmit={(output) => {
				onSave(output);
				close();
			}}
			state={state}
		>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">Edit Dashboard YAML</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<FormField className="drawer-body items-stretch" state={state}>
				<DashboardSaveAlert className="mb-4" placement="dashboard-yaml-drawer" />

				{(monitor, field) => (
					<div className="min-h-0 flex-1">
						<Editor
							height="100%"
							language="yaml"
							onChange={(input) => field.setInput(input)}
							value={field.getInput(monitor)}
						/>
					</div>
				)}

				{(monitor, field) => {
					const error = field.getError(monitor);

					if (error == null) {
						return null;
					}

					return (
						<FormMessage asChild unstyled variant="error">
							<Alert variant="error">{error}</Alert>
						</FormMessage>
					);
				}}
			</FormField>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<button className="button button-primary uppercase" type="submit">
					Update dashboard definition
				</button>
			</footer>
		</Form>
	);
};
